import React, { FC, ReactElement, useContext } from 'react'
import IconButton, { IconButtonType } from '../../Common/Components/IconButton'
import { IconName } from '../../Common/Components/Icon'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, PromptContext } from '../../Common/Contexts'
import { UsersByExtension } from '../../../../i18n'
import { PromptDisplayType } from '../../Common/Contexts/PromptContext'
import { LanguageUtils } from '../../Common/Utils/LanguageUtils'
import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import ContextMenu, { ContextMenuPosition } from '../../Common/Components/ContextMenu'
import { UserTypes } from '../../SelfProvisioning/Constants/Constants'

type Props = {
    addAllUsers: (userType: string, shouldSendWelcomeEmail: boolean) => Promise<void>
    telSysType?: TelSysTypes
    isAdminProvisioningUser: boolean
    availableExtensions: number
}

export const AddAllUsers: FC<Props> = ({
    addAllUsers,
    telSysType,
    availableExtensions,
    isAdminProvisioningUser
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler

    const { displayPromptPromise }: PromptDisplayType = useContext(PromptContext)

    const addAllUserTypeLanguage: Record<string, string> = {
        [UserTypes.ESSENTIALS]: UsersByExtension.UsersByExtension_UserType_ESSENTIALS,
        [UserTypes.PROFESSIONAL]: UsersByExtension.UsersByExtension_UserType_PROFESSIONAL
    }

    const addAllPrompt = (userType: string): void => {
        displayPromptPromise(
            LanguageUtils.formatLanguageEntry(
                language.getInterpolated(
                    UsersByExtension.AddAllUsers_PromptMessage,
                    language.getWithTelSysCheck(addAllUserTypeLanguage[userType], telSysType)
                )
            ),
            language.get(UsersByExtension.AddAllUsers_Confirm),
            language.get(UsersByExtension.AddAllUsers_Cancel),
            {
                checkMessage: LanguageUtils.formatLanguageEntry(
                    language.get(UsersByExtension.AddAllUsers_Send_Welcome_Email_Checkbox_Text)
                ),
                defaultChecked: true
            }
        ).then(
            async (shouldSendWelcomeEmail: Definable<boolean>): Promise<void> =>
                addAllUsers(userType, shouldSendWelcomeEmail ?? true)
        )
    }

    return (
        <div className='add-all-extensions'>
            <div className='add-all-icon-container'>
                <ContextMenu
                    menuItems={[
                        {
                            itemName: language.getWithTelSysCheck(
                                UsersByExtension.UsersByExtension_UserType_ESSENTIALS,
                                telSysType
                            ),
                            isEnabled: true,
                            onClick: (): void => addAllPrompt(UserTypes.ESSENTIALS)
                        },
                        {
                            itemName: language.getWithTelSysCheck(
                                UsersByExtension.UsersByExtension_UserType_PROFESSIONAL,
                                telSysType
                            ),
                            isEnabled: true,
                            onClick: (): void =>
                                addAllPrompt(
                                    isAdminProvisioningUser ? UserTypes.CALL_CENTRE_AGENT : UserTypes.PROFESSIONAL
                                )
                        }
                    ]}
                    position={ContextMenuPosition.BOTTOM_CENTER}
                    anchorElement={
                        <div className='edit-icon-wrapper'>
                            <IconButton
                                type={IconButtonType.BUTTON}
                                iconName={IconName.ADD_ALL}
                                isEnabled={availableExtensions > 0}
                                title={language.get(UsersByExtension.AddAllUsers_Button_Title)}
                            />
                        </div>
                    }
                />
            </div>
        </div>
    )
}
